import React, { useState, useEffect, useRef, useEffectEvent } from 'react';
import axios from 'axios';
import { MessageSquare, Users, BarChart3, Send, Phone, RefreshCw, ChevronRight, ChevronLeft, Package, FileText, Upload, Save, Pencil, X, BookOpen, Wrench, ClipboardList, Search, Download, Sun, Moon, Menu, LogOut, Megaphone, CalendarRange, FileSpreadsheet, Shuffle, KeyRound } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import CampaignManagerTab from './components/CampaignManagerTab';
import { downloadFile } from './lib/download';
import LeadImportTab from './components/LeadImportTab';
import RoutingTab from './components/RoutingTab';
import { routingLeadIdFromLocation } from './lib/routingService';
import CampaignMonthlyTab from './components/CampaignMonthlyTab';
import TemplatesTab from './components/TemplatesTab';
import LoginPage from './components/LoginPage';
import ChangePasswordPage from './components/ChangePasswordPage';
import StaffShell from './components/StaffShell';
import UsersTab from './components/UsersTab';
import { isAdminUser } from './lib/accountService';
import { BOT_TEST_CASES, buildBotTestReport, evaluateBotTestCase } from './lib/botTests';
import { API_URL } from './lib/api';
import {
  applyAuthHeader,
  clearAuth,
  fetchAuthStatus,
  getStoredToken,
  getStoredUser,
  persistAuth,
  verifySession,
} from './lib/auth';
import './App.css';

const DASHBOARD_REFRESH_MS = 30000;
const THEME_STORAGE_KEY = 'haique-theme';
const MOBILE_BREAKPOINT = 768;

function getIsMobileViewport() {
  return typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT;
}

function getInitialTheme() {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    /* ignore */
  }
  return 'light';
}
const MAX_PRODUCT_IMAGE_BYTES = 4 * 1024 * 1024;
const PRODUCT_PAGE_SIZE = 100;
const LEADS_PAGE_SIZE = 60;
const PRODUCT_TYPES = [
  'Quartz', 'Neo Marble', 'Terrazzo', 'Semi Precious',
  'Quartz Renders', 'Quartz Installation',
  'Neo Marble Renders', 'Neo Marble Installation',
  'Terrazzo Renders',
  'Dealer Showrooms',
];
const PRODUCT_SERIES = [
  'Dubai Series', 'London Series', 'Milan Series', 'New York Series',
  'Paris Series', 'Tokyo Series', 'Supreme Series',
  'Cafe Series', 'Rome Series', 'Semi Precious Collection',
];
const DEALER_SHOWROOM_LOCATIONS = [
  'Andhra Pradesh', 'Bangalore', 'Delhi', 'Hyderabad', 'Kerala',
  'Kishangarh', 'Kolkata', 'Raipur', 'Tiruchirapalli', 'Udaipur',
];
const BASE_COLOR_OPTIONS = [
  'White', 'Grey', 'Black', 'Beige', 'Brown', 'Green',
  'Cream', 'Blue', 'Gold', 'Pink', 'Silver', 'Indigo',
];
const EMPTY_PRODUCT_FORM = { name: '', type: '', category: '', price: '', buy_link: '', description: '', location: '', color_family: '' };

const pdfUrl = (kind, slug) => `${API_URL}/api/media/pdfs/${kind}/${slug}.pdf`;
const pdfFilename = (item) => `Haique ${item.label}.pdf`.replace(/[^\w .-]+/g, ' ').replace(/\s+/g, ' ').trim();
const pdfDownloadUrl = (item) => `${pdfUrl(item.kind, item.slug)}?download=1&filename=${encodeURIComponent(pdfFilename(item))}`;

const BROCHURES = [
  { label: 'Arc Collection',  kind: 'catalogue', slug: 'arc' },
  { label: 'Quartz',          kind: 'catalogue', slug: 'quartz' },
  { label: 'Neo Marble',      kind: 'catalogue', slug: 'neo-marble' },
  { label: 'Semi Precious',   kind: 'catalogue', slug: 'semi-precious' },
];

const INSTALLATION_MANUALS = [
  { label: 'Quartz',     kind: 'manual', slug: 'quartz' },
  { label: 'Neo Marble', kind: 'manual', slug: 'neo-marble' },
];

const TECHNICAL_SPECS = [
  { label: 'Quartz — Sheet 1', kind: 'technical-spec', slug: 'quartz-1' },
  { label: 'Quartz — Sheet 2', kind: 'technical-spec', slug: 'quartz-2' },
  { label: 'Quartz — Sheet 3', kind: 'technical-spec', slug: 'quartz-3' },
  { label: 'Quartz — Sheet 4', kind: 'technical-spec', slug: 'quartz-4' },
  { label: 'Neo Marble',       kind: 'technical-spec', slug: 'neo-marble' },
];

function buildManualRecSeed({ name, type, category }) {
  const n = (name || '').trim() || 'Product';
  const base = [type, category].map((p) => (p || '').trim()).filter(Boolean).join(' · ');
  return base ? `${n}. ${base}.` : `${n}.`;
}

function formatCatalogRecStored({ name, baseTags: _baseTags, extra }) {
  const n = (name || '').trim() || 'Design';
  const body = (extra || '').trim();
  return body || `${n}.`;
}

function extractCatalogRecExtra({ name, baseTags, stored }) {
  const text = (stored || '').trim();
  if (!text) return '';

  const n = (name || '').trim();
  const base = (baseTags || '').trim();
  const expectedHead = base ? `Name: ${n}\nBase tags: ${base}` : `Name: ${n}`;

  if (text.startsWith(expectedHead)) {
    return text.slice(expectedHead.length).replace(/^\n+/, '').trim();
  }
  return text
    .replace(/^Name:\s*/i, '')
    .replace(/^Base tags:\s.*$/gim, '')
    .replace(/^\n+/, '')
    .trim();
}

function validateProductImage(file) {
  if (!file) return true;
  if (file.size <= MAX_PRODUCT_IMAGE_BYTES) return true;
  toast.error('Product image must be 4 MB or smaller');
  return false;
}

function enrichManualProductClient(product) {
  const p = product || {};
  const colorFamily = Array.isArray(p.color_family) ? p.color_family.filter(Boolean) : [];
  const baseTags = [p.type, p.category, p.collection, p.product_key]
    .map((x) => (x || '').trim()).filter(Boolean).join(' · ');
  // Support both old single image_url and new image_urls array from seeded products
  const image_url = p.image_url || (Array.isArray(p.image_urls) ? p.image_urls[0] : '') || '';
  return {
    ...p,
    image_url,
    color_family: colorFamily,
    baseTags,
    recSeed: buildManualRecSeed({ name: p.name, type: p.type, category: p.category }),
  };
}

function App() {
  const [theme, setTheme] = useState(getInitialTheme);
  const [authUser, setAuthUser] = useState(() => getStoredUser());
  const [authChecking, setAuthChecking] = useState(true);
  const [loginRequired, setLoginRequired] = useState(true);
  const [authConfigured, setAuthConfigured] = useState(true);
  const [isMobile, setIsMobile] = useState(getIsMobileViewport);
  const [sidebarOpen, setSidebarOpen] = useState(() => !getIsMobileViewport());
  // The WhatsApp alert links to /routing/leads/<id>; open that lead straight away.
  const [deepLinkLeadId, setDeepLinkLeadId] = useState(() => routingLeadIdFromLocation());
  const [activeTab, setActiveTab] = useState(() => (routingLeadIdFromLocation() ? 'routing' : 'conversations'));
  const [conversations, setConversations] = useState([]);
  const [conversationSearch, setConversationSearch] = useState('');
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [leads, setLeads] = useState([]);
  const [leadsPage, setLeadsPage] = useState(0);
  const [leadsTotal, setLeadsTotal] = useState(0);
  const [leadsSearch, setLeadsSearch] = useState('');
  const [leadsCategory, setLeadsCategory] = useState('');
  const [leadsSource, setLeadsSource] = useState('');
  const [leadsStatusFilter, setLeadsStatusFilter] = useState('');
  const [leadsExcluded, setLeadsExcluded] = useState(false);
  const [leadsMeta, setLeadsMeta] = useState({ categories: [], sources: [], lead_statuses: [] });
  const [stats, setStats] = useState({});
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [products, setProducts] = useState([]);
  const [productPage, setProductPage] = useState(0);
  const [productTotal, setProductTotal] = useState(0);
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);
  const [productSaving, setProductSaving] = useState(false);
  const [productForm, setProductForm] = useState(EMPTY_PRODUCT_FORM);
  const [productFormImage, setProductFormImage] = useState(null);
  /** When true, manual product edit UI is shown on the product card (not the top "Add" form). */
  const [manualEditInline, setManualEditInline] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [catalogUploadFile, setCatalogUploadFile] = useState(null);
  const [catalogUploading, setCatalogUploading] = useState(false);
  const [catalogRecEditor, setCatalogRecEditor] = useState({
    open: false,
    designId: '',
    productName: '',
    productLabel: '',
    baseTags: '',
    extra: '',
  });
  const [catalogRecSaving, setCatalogRecSaving] = useState(false);
  const [promptText, setPromptText] = useState('');
  const [promptSaving, setPromptSaving] = useState(false);
  const [promptLoaded, setPromptLoaded] = useState(false);
  const [promptDirty, setPromptDirty] = useState(false);
  const [botTestResults, setBotTestResults] = useState([]);
  const [botTesting, setBotTesting] = useState(false);
  const [botTestReport, setBotTestReport] = useState('');
  const messagesEndRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const isNearBottomRef = useRef(true);
  const selectedPhone = selectedConversation?.phone || '';
  const conversationSearchTerm = conversationSearch.trim().toLowerCase();
  const filteredConversations = conversationSearchTerm
    ? conversations.filter((conv) => {
        const haystack = [
          conv.name,
          conv.phone,
          conv.last_message,
        ].map((value) => String(value || '').toLowerCase()).join(' ');
        return haystack.includes(conversationSearchTerm);
      })
    : conversations;

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      /* ignore */
    }
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
      meta.setAttribute('content', theme === 'dark' ? '#0C0828' : '#1F0798');
    }
  }, [theme]);

  useEffect(() => {
    let cancelled = false;
    const bootAuth = async () => {
      try {
        const status = await fetchAuthStatus();
        if (!cancelled) {
          setLoginRequired(status.loginRequired !== false);
          setAuthConfigured(status.configured !== false);
        }
      } catch {
        if (!cancelled) {
          setLoginRequired(true);
          setAuthConfigured(false);
        }
      }

      const token = getStoredToken();
      if (!token) {
        applyAuthHeader('');
        if (!cancelled) {
          setAuthUser(null);
          setAuthChecking(false);
        }
        return;
      }
      applyAuthHeader(token);
      try {
        const user = await verifySession(token);
        if (!cancelled) {
          setAuthUser(user);
          persistAuth(token, user);
        }
      } catch {
        clearAuth();
        applyAuthHeader('');
        if (!cancelled) setAuthUser(null);
      } finally {
        if (!cancelled) setAuthChecking(false);
      }
    };
    bootAuth();
    return () => { cancelled = true; };
  }, []);

  const handleLogin = (token, user) => {
    persistAuth(token, user);
    applyAuthHeader(token);
    setAuthUser(user);
  };

  const handleLogout = () => {
    clearAuth();
    applyAuthHeader('');
    setAuthUser(null);
    toast.success('Signed out');
  };

  const toggleTheme = () => {
    setTheme((current) => (current === 'light' ? 'dark' : 'light'));
  };

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      if (!mobile) setSidebarOpen(true);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const selectTab = (tab) => {
    setActiveTab(tab);
    if (isMobile) setSidebarOpen(false);
  };

  const toggleSidebar = () => setSidebarOpen((open) => !open);
  const closeSidebar = () => setSidebarOpen(false);

  const showMobileChat = isMobile && activeTab === 'conversations' && selectedConversation;

  const adminView = !!authUser && isAdminUser(authUser);

  useEffect(() => {
    if (!adminView) return;
    fetchStats();
    if (activeTab === 'conversations') {
      fetchConversations();
    } else if (activeTab === 'leads') {
      fetchLeadsMeta();
    } else if (activeTab === 'prompts') {
      if (!promptLoaded) fetchPrompt();
    }
  }, [adminView, activeTab, promptLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab !== 'products') return undefined;
    const timer = window.setTimeout(() => {
      fetchProducts({ page: 0, search: productSearch });
    }, 250);
    return () => window.clearTimeout(timer);
  }, [activeTab, productSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab !== 'leads') return undefined;
    const timer = window.setTimeout(() => {
      fetchLeads({ page: 0, search: leadsSearch, category: leadsCategory, source: leadsSource, leadStatus: leadsStatusFilter });
    }, 250);
    return () => window.clearTimeout(timer);
  }, [activeTab, leadsSearch, leadsCategory, leadsSource, leadsStatusFilter, leadsExcluded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedPhone && activeTab === 'conversations') {
      isNearBottomRef.current = true;
      fetchMessages(selectedPhone);
    }
  }, [activeTab, selectedPhone]);


  useEffect(() => {
    if (manualEditInline && editingProductId) {
      requestAnimationFrame(() => {
        document.getElementById(`manual-card-${editingProductId}`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    }
  }, [manualEditInline, editingProductId]);

  useEffect(() => {
    if (catalogRecEditor.open && catalogRecEditor.designId) {
      requestAnimationFrame(() => {
        document.getElementById(`catalog-card-${catalogRecEditor.designId}`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    }
  }, [catalogRecEditor.open, catalogRecEditor.designId]);

  useEffect(() => {
    if (isNearBottomRef.current) {
      scrollToBottom();
    }
  }, [messages]);

  const runBackgroundRefresh = useEffectEvent(() => {
    if (!adminView) return;
    fetchStats({ showError: false });

    if (activeTab === 'conversations') {
      fetchConversations({ showError: false, background: true });
      if (selectedPhone) {
        fetchMessages(selectedPhone, { showError: false });
      }
      return;
    }

    if (activeTab === 'leads') {
      fetchLeads({ showError: false, background: true });
    }
  });

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      if (document.hidden) {
        return;
      }
      runBackgroundRefresh();
    }, DASHBOARD_REFRESH_MS);

    return () => window.clearInterval(intervalId);
  }, [runBackgroundRefresh]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleChatScroll = () => {
    const el = chatMessagesRef.current;
    if (!el) return;
    const threshold = 80;
    isNearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
  };

  const fetchStats = async ({ showError = false } = {}) => {
    try {
      const res = await axios.get(`${API_URL}/api/stats`);
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      if (showError) {
        toast.error('Failed to fetch stats');
      }
    }
  };

  const fetchConversations = async ({ showError = true, background = false } = {}) => {
    if (!background) {
      setLoading(true);
    }
    try {
      const res = await axios.get(`${API_URL}/api/conversations`);
      const nextConversations = res.data || [];
      setConversations(nextConversations);
      setSelectedConversation((current) => {
        if (!current) {
          return current;
        }
        return nextConversations.find((conv) => conv.phone === current.phone) || current;
      });
    } catch (error) {
      console.error('Error fetching conversations:', error);
      if (showError) {
        toast.error('Failed to fetch conversations');
      }
    } finally {
      if (!background) {
        setLoading(false);
      }
    }
  };

  const fetchMessages = async (phone, { showError = true } = {}) => {
    try {
      const res = await axios.get(`${API_URL}/api/conversations/${phone}`);
      const msgs = Array.isArray(res.data) ? res.data : res.data?.messages ?? [];
      setMessages(msgs);
    } catch (error) {
      console.error('Error fetching messages:', error);
      if (showError) {
        toast.error('Failed to fetch messages');
      }
    }
  };

  const fetchLeads = async ({
    showError = true,
    background = false,
    page = leadsPage,
    search = leadsSearch,
    category = leadsCategory,
    source = leadsSource,
    leadStatus = leadsStatusFilter,
  } = {}) => {
    if (!background) {
      setLoading(true);
    }
    try {
      const params = { skip: page * LEADS_PAGE_SIZE, limit: LEADS_PAGE_SIZE };
      if (search.trim()) params.search = search.trim();
      if (category) params.category = category;
      if (source) params.source = source;
      if (leadStatus) params.lead_status = leadStatus;
      if (leadsExcluded) params.excluded = true;
      const res = await axios.get(`${API_URL}/api/leads`, { params });
      const data = res.data?.data ?? res.data ?? {};
      const leadsData = data?.items ?? (Array.isArray(data) ? data : []);
      setLeads(leadsData);
      setLeadsTotal(data?.total ?? leadsData.length);
      setLeadsPage(page);
      setLeadsSearch(search);
      setLeadsCategory(category);
      setLeadsSource(source);
      setLeadsStatusFilter(leadStatus);
    } catch (error) {
      console.error('Error fetching leads:', error);
      if (showError) {
        toast.error('Failed to fetch leads');
      }
    } finally {
      if (!background) {
        setLoading(false);
      }
    }
  };

  const fetchLeadsMeta = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/leads/meta`);
      const data = res.data?.data ?? res.data ?? {};
      setLeadsMeta({
        categories: data.categories || [],
        sources: data.sources || [],
        lead_statuses: data.lead_statuses || [],
      });
    } catch (error) {
      console.error('Error fetching lead filters:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation || isSending) return;

    try {
      setIsSending(true);
      const response = await axios.post(`${API_URL}/api/whatsapp/send`, {
        phone: selectedConversation.phone,
        message: newMessage
      });
      if (!response.data?.success) {
        throw new Error(response.data?.error || 'Failed to send message');
      }
      toast.success('Message sent!');
      setNewMessage('');
      setTimeout(() => {
        fetchMessages(selectedConversation.phone, { showError: false });
        fetchConversations({ showError: false, background: true });
        fetchStats({ showError: false });
      }, 500);
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.error ||
        error.message ||
        'Failed to send message';
      toast.error(errorMessage);
    } finally {
      setIsSending(false);
    }
  };

  const refreshConversationView = () => {
    fetchConversations();
    fetchStats({ showError: false });
    if (selectedPhone) {
      fetchMessages(selectedPhone, { showError: false });
    }
  };

  const refreshLeadsView = () => {
    fetchLeads({ page: leadsPage });
    fetchLeadsMeta();
    fetchStats({ showError: false });
  };

  const fetchProducts = async ({ page = productPage, search = productSearch } = {}) => {
    setLoading(true);
    try {
      const params = {
        skip: page * PRODUCT_PAGE_SIZE,
        limit: PRODUCT_PAGE_SIZE,
      };
      const q = (search || '').trim();
      if (q) params.q = q;
      const productsRes = await axios.get(`${API_URL}/api/products`, { params });
      const raw = productsRes.data?.data ?? productsRes.data ?? [];
      const enriched = (Array.isArray(raw) ? raw : []).map((p) => enrichManualProductClient(p));
      setProducts(enriched);
      setProductPage(page);
      setProductTotal(productsRes.data?.meta?.total ?? enriched.length);
      setCatalogProducts([]);
      // Populate collection datalist from loaded products
      const uniqueCollections = [...new Set(enriched.map((p) => p.category || p.collection).filter(Boolean))];
      setCollections(uniqueCollections);
    } catch (error) {
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const exportProductsCSV = async () => {
    toast.info('Preparing export…');
    try {
      const PAGE = 200;
      const all = [];
      let skip = 0;
      while (true) {
        const res = await axios.get(`${API_URL}/api/products`, { params: { skip, limit: PAGE } });
        const page = res.data?.data ?? res.data ?? [];
        if (!Array.isArray(page) || !page.length) break;
        all.push(...page);
        if (page.length < PAGE) break;
        skip += PAGE;
      }
      if (!all.length) { toast.error('No products to export'); return; }
      const headers = ['id', 'name', 'type', 'category', 'collection', 'price', 'buy_link', 'description', 'location', 'color_family', 'image_url'];
      const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const rows = all.map((p) => [
        p.id, p.name, p.type, p.category, p.collection,
        p.price, p.buy_link, p.description, p.location,
        Array.isArray(p.color_family) ? p.color_family.join('; ') : (p.color_family || ''),
        p.image_url,
      ].map(escape).join(','));
      const csv = [headers.join(','), ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `haique-products-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${all.length} products`);
    } catch {
      toast.error('Export failed');
    }
  };

  const openCatalogRecommendationEditor = (product) => {
    const name = product.name || '';
    const baseTags = product.baseTags || '';
    const stored = (product.recommendationText || '').trim();
    setCatalogRecEditor({
      open: true,
      designId: product.designId,
      productName: name,
      productLabel: `${product.name} — ${product.category}`,
      baseTags,
      extra: extractCatalogRecExtra({ name, baseTags, stored }),
    });
  };

  const closeCatalogRecommendationEditor = () => {
    setCatalogRecEditor((prev) => ({ ...prev, open: false }));
  };

  const saveCatalogRecommendation = async () => {
    if (!catalogRecEditor.designId) return;
    setCatalogRecSaving(true);
    try {
      const stored = formatCatalogRecStored({
        name: catalogRecEditor.productName,
        baseTags: catalogRecEditor.baseTags,
        extra: catalogRecEditor.extra,
      });
      await axios.put(`${API_URL}/api/catalog/designs/${catalogRecEditor.designId}/recommendation`, {
        description: stored,
      });
      setCatalogProducts((prev) =>
        prev.map((p) => {
          if (p.designId !== catalogRecEditor.designId) return p;
          const rec = stored;
          return {
            ...p,
            recommendationText: rec,
            description: rec,
          };
        })
      );
      closeCatalogRecommendationEditor();
      toast.success('Recommendation text saved for this catalog product');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save recommendation text');
    } finally {
      setCatalogRecSaving(false);
    }
  };

  const deleteCatalogRecommendation = async (product) => {
    if (!product?.designId) return;
    if (!window.confirm('Remove the saved recommendation text for this catalog product? (Catalog data stays; bot will rely on the default tags again.)')) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/api/catalog/designs/${product.designId}/recommendation`);
      setCatalogProducts((prev) =>
        prev.map((p) => {
          if (p.designId !== product.designId) return p;
          const displayText = formatCatalogRecStored({ name: p.name, baseTags: p.baseTags, extra: '' });
          return { ...p, recommendationText: '', description: displayText };
        })
      );
      setCatalogRecEditor((cur) => (cur.open && cur.designId === product.designId ? { ...cur, open: false } : cur));
      toast.success('Removed saved recommendation text');
    } catch (error) {
      toast.error('Failed to delete recommendation text');
    }
  };


  const resetProductForm = () => {
    setProductForm(EMPTY_PRODUCT_FORM);
    setProductFormImage(null);
    setEditingProductId(null);
  };

  const startAddProduct = () => {
    if (showAddForm && !editingProductId) {
      setShowAddForm(false);
      return;
    }
    resetProductForm();
    setManualEditInline(false);
    setShowAddForm(true);
  };

  const startEditProduct = (product) => {
    const seed =
      product.recSeed ||
      buildManualRecSeed({ name: product.name, type: product.type, category: product.category });
    setProductForm({
      name: product.name || '',
      type: product.type || '',
      category: product.category || '',
      price: product.price || '',
      buy_link: product.buy_link || '',
      description: (product.description || '').trim() || seed,
      location: product.location || '',
      color_family: Array.isArray(product.color_family) ? product.color_family.join(', ') : '',
    });
    setProductFormImage(null);
    setEditingProductId(product.id);
    setShowAddForm(false);
    setManualEditInline(true);
  };

  const closeProductForm = () => {
    setShowAddForm(false);
    setManualEditInline(false);
    resetProductForm();
  };

  const handleSaveProduct = async () => {
    if (!productForm.name.trim()) {
      toast.error('Product name is required');
      return;
    }
    if (!validateProductImage(productFormImage)) {
      return;
    }
    setProductSaving(true);
    try {
      const desc =
        (productForm.description || '').trim() ||
        buildManualRecSeed({ name: productForm.name, type: productForm.type, category: productForm.category });
      const formData = new FormData();
      formData.append('name', productForm.name);
      formData.append('type', productForm.type);
      formData.append('category', productForm.category || productForm.location || productForm.type);
      formData.append('price', productForm.price);
      formData.append('buy_link', productForm.buy_link);
      formData.append('color_family', productForm.color_family);
      formData.append('description', desc);
      if (productForm.location) formData.append('location', productForm.location);
      if (productFormImage) formData.append('image', productFormImage);

      const res = editingProductId
        ? await axios.patch(`${API_URL}/api/products/${editingProductId}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
        : await axios.post(`${API_URL}/api/products`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });

      const saved = res.data?.data ?? res.data;
      setProducts((prev) => {
        if (!editingProductId) {
          return [enrichManualProductClient(saved), ...prev];
        }
        return prev.map((product) =>
          product.id === editingProductId ? enrichManualProductClient(saved) : product
        );
      });
      closeProductForm();
      toast.success(editingProductId ? 'Product updated' : 'Product added');
    } catch (error) {
      toast.error(error.response?.data?.detail || (editingProductId ? 'Failed to update product' : 'Failed to add product'));
    } finally {
      setProductSaving(false);
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Delete this product from the bot catalog?')) return;
    try {
      await axios.delete(`${API_URL}/api/products/${id}`);
      setProducts((prev) => prev.filter((p) => p.id !== id));
      if (editingProductId === id) {
        closeProductForm();
      }
      toast.success('Product deleted');
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const fetchPrompt = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/prompt`);
      setPromptText(res.data.prompt || '');
      setPromptLoaded(true);
      setPromptDirty(false);
    } catch (error) {
      toast.error('Failed to fetch prompt');
    }
  };

  const handleSavePrompt = async () => {
    if (!promptText.trim()) return;
    setPromptSaving(true);
    try {
      await axios.post(`${API_URL}/api/prompt`, { prompt: promptText });
      setPromptDirty(false);
      toast.success('Prompt saved — bot is using the new prompt instantly');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to save prompt';
      toast.error(msg);
    } finally {
      setPromptSaving(false);
    }
  };

  const handleUploadCatalog = async () => {
    if (!catalogUploadFile || catalogUploading) return;
    setCatalogUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', catalogUploadFile);
      const res = await axios.post(`${API_URL}/api/catalog/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCatalogUploadFile(null);
      await fetchProducts();
      toast.success(`Catalog uploaded (${res.data?.designs || 0} designs)`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload catalog JSON');
    } finally {
      setCatalogUploading(false);
    }
  };

  const runBotTests = async () => {
    setBotTesting(true);
    setBotTestResults([]);
    setBotTestReport('');
    try {
      const results = [];
      for (const testCase of BOT_TEST_CASES) {
        const res = await axios.get(`${API_URL}/api/catalog/search`, {
          params: { q: testCase.query, limit: 5 },
        });
        const evaluation = evaluateBotTestCase(testCase, res.data);
        results.push({
          ...testCase,
          ...evaluation,
          replyType: res.data?.catalog_reply?.type || '',
        });
      }
      setBotTestResults(results);
      setBotTestReport(buildBotTestReport(results));
      const failed = results.filter((result) => !result.passed).length;
      if (failed) {
        toast.error(`${failed} bot check${failed === 1 ? '' : 's'} failed`);
      } else {
        toast.success('All bot checks passed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to run bot tests');
    } finally {
      setBotTesting(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const resolveMediaUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
      return url;
    }
    return `${API_URL}${url}`;
  };

  if (authChecking) {
    return (
      <div className="login-page executive-app login-page--loading">
        <div className="login-shell">
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  if (authUser && authUser.must_change_password) {
    return (
      <>
        <ChangePasswordPage apiUrl={API_URL} user={authUser} onChanged={handleLogin} onLogout={handleLogout} />
        <Toaster position="top-right" richColors />
      </>
    );
  }

  if (authUser && !isAdminUser(authUser)) {
    return (
      <>
        <StaffShell
          apiUrl={API_URL}
          user={authUser}
          theme={theme}
          onToggleTheme={toggleTheme}
          onLogout={handleLogout}
          onChangePassword={() => setAuthUser({ ...authUser, must_change_password: true })}
          initialLeadId={deepLinkLeadId}
          onInitialHandled={() => setDeepLinkLeadId('')}
        />
        <Toaster position="top-right" richColors />
      </>
    );
  }

  if (!authUser) {
    return (
      <>
        <LoginPage
          theme={theme}
          onToggleTheme={toggleTheme}
          onLogin={handleLogin}
          authConfigured={authConfigured}
        />
        <Toaster position="top-right" richColors />
      </>
    );
  }

  return (
    <div className="app-container executive-app">
      <div className="clara-bg-layer" aria-hidden="true">
        <img src={`${process.env.PUBLIC_URL}/favicon.svg`} alt="" className="clara-bg-mark clara-bg-mark--1" />
        <div className="clara-bg-bloom clara-bg-bloom--lavender" />
      </div>
      <Toaster position="top-right" richColors />

      <button
        type="button"
        className="dashboard-theme-toggle"
        onClick={toggleTheme}
        aria-label={theme === 'light' ? 'Switch to dark theme' : 'Switch to light theme'}
        data-testid="theme-toggle"
      >
        {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
      </button>

      {isMobile && sidebarOpen && (
        <button
          type="button"
          className="sidebar-overlay"
          onClick={closeSidebar}
          aria-label="Close navigation menu"
        />
      )}
      
      {/* Sidebar */}
      <aside className={`sidebar${sidebarOpen ? ' sidebar-open' : ''}`}>
        <div className="sidebar-top-row">
          <div className="logo">
            <div className="logo-icon">H</div>
            <span className="logo-text">Haique Bot</span>
          </div>
          {isMobile && (
            <button
              type="button"
              className="sidebar-close-btn"
              onClick={closeSidebar}
              aria-label="Close menu"
            >
              <X size={20} />
            </button>
          )}
        </div>
        
        <nav className="nav-menu">
          <button 
            data-testid="nav-conversations"
            className={`nav-item ${activeTab === 'conversations' ? 'active' : ''}`}
            onClick={() => selectTab('conversations')}
          >
            <MessageSquare size={20} />
            <span>Conversations</span>
          </button>
          <button 
            data-testid="nav-leads"
            className={`nav-item ${activeTab === 'leads' ? 'active' : ''}`}
            onClick={() => selectTab('leads')}
          >
            <Users size={20} />
            <span>Leads</span>
          </button>
          <button
            data-testid="nav-stats"
            className={`nav-item ${activeTab === 'stats' ? 'active' : ''}`}
            onClick={() => selectTab('stats')}
          >
            <BarChart3 size={20} />
            <span>Stats</span>
          </button>
          <button
            data-testid="nav-routing"
            className={`nav-item ${activeTab === 'routing' ? 'active' : ''}`}
            onClick={() => selectTab('routing')}
          >
            <Shuffle size={20} />
            <span>Lead Routing</span>
          </button>
          <button
            data-testid="nav-users"
            className={`nav-item ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => selectTab('users')}
          >
            <KeyRound size={20} />
            <span>Team &amp; Access</span>
          </button>
          <button
            data-testid="nav-lead-import"
            className={`nav-item ${activeTab === 'lead-import' ? 'active' : ''}`}
            onClick={() => selectTab('lead-import')}
          >
            <FileSpreadsheet size={20} />
            <span>Import Leads</span>
          </button>
          <button
            data-testid="nav-campaign-manager"
            className={`nav-item ${activeTab === 'campaign-manager' ? 'active' : ''}`}
            onClick={() => selectTab('campaign-manager')}
          >
            <Megaphone size={20} />
            <span>Campaign Manager</span>
          </button>
          <button
            data-testid="nav-campaign-monthly"
            className={`nav-item ${activeTab === 'campaign-monthly' ? 'active' : ''}`}
            onClick={() => selectTab('campaign-monthly')}
          >
            <CalendarRange size={20} />
            <span>Monthly Overview</span>
          </button>
          <button
            data-testid="nav-templates"
            className={`nav-item ${activeTab === 'templates' ? 'active' : ''}`}
            onClick={() => selectTab('templates')}
          >
            <FileText size={20} />
            <span>Templates</span>
          </button>
          <button
            data-testid="nav-products"
            className={`nav-item ${activeTab === 'products' ? 'active' : ''}`}
            onClick={() => selectTab('products')}
          >
            <Package size={20} />
            <span>Products</span>
          </button>
          <button
            data-testid="nav-prompts"
            className={`nav-item ${activeTab === 'prompts' ? 'active' : ''}`}
            onClick={() => selectTab('prompts')}
          >
            <FileText size={20} />
            <span>Prompt</span>
          </button>
          <button
            data-testid="nav-brochures"
            className={`nav-item ${activeTab === 'brochures' ? 'active' : ''}`}
            onClick={() => selectTab('brochures')}
          >
            <BookOpen size={20} />
            <span>Brochures</span>
          </button>
          <button
            data-testid="nav-installation"
            className={`nav-item ${activeTab === 'installation' ? 'active' : ''}`}
            onClick={() => selectTab('installation')}
          >
            <Wrench size={20} />
            <span>Installation</span>
          </button>
          <button
            data-testid="nav-techspecs"
            className={`nav-item ${activeTab === 'techspecs' ? 'active' : ''}`}
            onClick={() => selectTab('techspecs')}
          >
            <ClipboardList size={20} />
            <span>Tech Specs</span>
          </button>
        </nav>

        {authUser ? (
          <button
            type="button"
            className="logout-btn"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut size={18} />
            <span>Sign out</span>
          </button>
        ) : null}

        <div className="stats-mini">
          <div className="stat-item">
            <span className="stat-value">{stats.total_users || 0}</span>
            <span className="stat-label">Users</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{stats.total_leads || 0}</span>
            <span className="stat-label">Leads</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{stats.total_messages || 0}</span>
            <span className="stat-label">Messages</span>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`main-content${['campaign-manager', 'campaign-monthly', 'lead-import', 'users', 'routing', 'stats', 'templates'].includes(activeTab) ? ' page-scroll' : ''}`}>
        <div className="mobile-topbar">
          <button
            type="button"
            className="sidebar-toggle"
            onClick={toggleSidebar}
            aria-label={sidebarOpen ? 'Hide menu' : 'Show menu'}
            data-testid="sidebar-toggle"
          >
            <Menu size={22} />
          </button>
          <span className="mobile-topbar-title">Haique Bot</span>
        </div>

        {activeTab === 'conversations' && (
          <div className={`conversations-layout${showMobileChat ? ' mobile-chat-active' : ''}`}>
            {/* Conversation List */}
            <div className={`conversation-list${showMobileChat ? ' mobile-panel-hidden' : ''}`}>
              <div className="list-header">
                <div className="list-title">
                  <h2>Conversations</h2>
                  <span>
                    {conversationSearchTerm
                      ? `${filteredConversations.length} of ${conversations.length} users`
                      : `${conversations.length} users`}
                  </span>
                </div>
                <button data-testid="refresh-btn" className="refresh-btn" onClick={refreshConversationView}>
                  <RefreshCw size={18} />
                </button>
              </div>
              <div className="conversation-search">
                <Search size={16} />
                <input
                  data-testid="conversation-search"
                  type="search"
                  placeholder="Search users by name, phone, or message"
                  value={conversationSearch}
                  onChange={(e) => setConversationSearch(e.target.value)}
                />
              </div>
              
              {loading ? (
                <div className="loading">Loading...</div>
              ) : conversations.length === 0 ? (
                <div className="empty-state">
                  <MessageSquare size={48} />
                  <p>No conversations yet</p>
                  <span>Messages will appear here when customers chat with the bot</span>
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="empty-state compact">
                  <Search size={40} />
                  <p>No users found</p>
                  <span>Try another name, phone number, or message keyword</span>
                </div>
              ) : (
                <div className="list-items">
                  {filteredConversations.map((conv) => (
                    <div
                      key={conv.phone}
                      data-testid={`conversation-${conv.phone}`}
                      className={`conversation-item ${selectedConversation?.phone === conv.phone ? 'selected' : ''}`}
                      onClick={() => setSelectedConversation(conv)}
                    >
                      <div className="conv-avatar">
                        {(conv.name || 'C')[0].toUpperCase()}
                      </div>
                      <div className="conv-details">
                        <div className="conv-header">
                          <span className="conv-name">{conv.name || 'Customer'}</span>
                          <span className="conv-time">{formatTime(conv.last_message_at)}</span>
                        </div>
                        <div className="conv-preview">
                          <Phone size={12} />
                          <span>{conv.phone}</span>
                        </div>
                        <p className="conv-last-msg">
                          {(conv.last_message || '').replace(/^\[(list|buttons|document|image|template|document-fallback)\]\s*/i, '')}
                        </p>
                      </div>
                      <ChevronRight size={16} className="conv-arrow" />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Chat Window */}
            <div className="chat-window">
              {selectedConversation ? (
                <>
                  <div className="chat-header">
                    {isMobile && (
                      <button
                        type="button"
                        className="mobile-back-btn"
                        onClick={() => setSelectedConversation(null)}
                        aria-label="Back to conversations"
                      >
                        <ChevronLeft size={22} />
                      </button>
                    )}
                    <div className="chat-user-info">
                      <div className="chat-avatar">
                        {(selectedConversation.name || 'C')[0].toUpperCase()}
                      </div>
                      <div>
                        <h3>{selectedConversation.name || 'Customer'}</h3>
                        <span>{selectedConversation.phone}</span>
                      </div>
                    </div>
                  </div>

                  <div className="chat-messages" ref={chatMessagesRef} onScroll={handleChatScroll}>
                    {messages.map((msg, idx) => (
                      <div
                        key={idx}
                        className={`message ${msg.direction === 'outbound' ? 'outbound' : 'inbound'}`}
                      >
                        <div className="message-content">
                          {msg.message_type === 'image' ? (() => {
                            const imgUrl = msg.media_url || (msg.content && msg.content.match(/^\[image\]\s*(https?:\/\/\S+)/)?.[1]);
                            const caption = msg.media_url ? msg.content : null;
                            return imgUrl ? (
                              <>
                                <img
                                  className="message-image"
                                  src={resolveMediaUrl(imgUrl)}
                                  alt={caption || 'Catalog image'}
                                />
                                {caption ? <p>{caption}</p> : null}
                              </>
                            ) : <p>{msg.content}</p>;
                          })() : msg.message_type === 'list' ? (
                            <>
                              <p>{(msg.content || '').replace(/^\[list\]\s*/i, '')}</p>
                              <span className="message-type-tag">📋 Menu</span>
                            </>
                          ) : msg.message_type === 'buttons' ? (
                            <>
                              <p>{(msg.content || '').replace(/^\[buttons\]\s*/i, '')}</p>
                              <span className="message-type-tag">🔘 Options</span>
                            </>
                          ) : msg.message_type === 'document' ? (
                            <p>📄 {(msg.content || '').replace(/^\[document(?:-fallback)?\]\s*/i, '')}</p>
                          ) : msg.message_type === 'template' ? (
                            <p>📨 {(msg.content || '').replace(/^\[template\]\s*/i, '')}</p>
                          ) : (
                            <p>{msg.content}</p>
                          )}
                          <span className="message-time">{formatTime(msg.timestamp)}</span>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>

                  <div className="chat-input">
                    <input
                      data-testid="message-input"
                      type="text"
                      placeholder="Type a message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          sendMessage();
                        }
                      }}
                      disabled={isSending}
                    />
                    <button
                      data-testid="send-btn"
                      className="send-btn"
                      onClick={sendMessage}
                      disabled={isSending || !newMessage.trim()}
                      aria-label={isSending ? 'Sending message' : 'Send message'}
                    >
                      <Send size={20} />
                    </button>
                  </div>
                </>
              ) : (
                <div className="chat-empty">
                  <div className="clara-abstract" aria-hidden="true">
                    <div className="clara-abstract-ring clara-abstract-ring--1" />
                    <div className="clara-abstract-ring clara-abstract-ring--2" />
                    <div className="clara-abstract-ring clara-abstract-ring--3" />
                    <img
                      src={`${process.env.PUBLIC_URL}/favicon.svg`}
                      alt=""
                      className="clara-abstract-mark"
                    />
                  </div>
                  <span className="clara-brand">Clara.ai</span>
                  <h3>Select a conversation</h3>
                  <p>Choose a conversation from the list to view messages</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'leads' && (
          <div className="leads-section">
            <div className="section-header">
              <h2>Leads ({leadsTotal})</h2>
              <div className="section-header-actions">
                <button
                  type="button"
                  data-testid="export-leads-btn"
                  className="export-btn"
                  onClick={async () => {
                    try {
                      await downloadFile(`${API_URL}/api/leads/export/csv`, 'leads.csv');
                    } catch {
                      toast.error('Export failed');
                    }
                  }}
                >
                  <Download size={16} />
                  <span>Export CSV</span>
                </button>
                <button data-testid="refresh-leads-btn" className="refresh-btn" onClick={refreshLeadsView}>
                  <RefreshCw size={18} />
                </button>
              </div>
            </div>

            <div className="leads-filter-bar">
              <div className="campaign-search">
                <Search size={16} />
                <input
                  type="search"
                  placeholder="Search leads by name or phone…"
                  value={leadsSearch}
                  onChange={(e) => setLeadsSearch(e.target.value)}
                />
              </div>
              <select className="campaign-status-filter" value={leadsCategory} onChange={(e) => setLeadsCategory(e.target.value)}>
                <option value="">All categories</option>
                {leadsMeta.categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <select className="campaign-status-filter" value={leadsSource} onChange={(e) => setLeadsSource(e.target.value)}>
                <option value="">All sources</option>
                {leadsMeta.sources.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <select className="campaign-status-filter" value={leadsStatusFilter} onChange={(e) => setLeadsStatusFilter(e.target.value)}>
                <option value="">All lead statuses</option>
                {leadsMeta.lead_statuses.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <select
                className="campaign-status-filter"
                value={leadsExcluded ? 'excluded' : ''}
                onChange={(e) => setLeadsExcluded(e.target.value === 'excluded')}
                data-testid="leads-excluded-filter"
              >
                <option value="">All numbers</option>
                <option value="excluded">Excluded from campaigns (landline, shared, fake)</option>
              </select>
              {(leadsCategory || leadsSource || leadsStatusFilter || leadsSearch || leadsExcluded) && (
                <button
                  type="button"
                  className="campaign-back-btn"
                  onClick={() => { setLeadsCategory(''); setLeadsSource(''); setLeadsStatusFilter(''); setLeadsSearch(''); setLeadsExcluded(false); }}
                >
                  Clear filters
                </button>
              )}
            </div>

            {loading ? (
              <div className="loading">Loading...</div>
            ) : leads.length === 0 ? (
              <div className="empty-state">
                <Users size={48} />
                <p>No leads found</p>
                <span>
                  {leadsCategory || leadsSource || leadsStatusFilter || leadsSearch
                    ? 'No leads match these filters — try clearing them.'
                    : 'Leads will be automatically captured from WhatsApp conversations, or import them from Excel in Campaign Manager.'}
                </span>
              </div>
            ) : (
              <>
                <div className="leads-grid">
                  {leads.map((lead, index) => (
                    <div key={lead.id} data-testid={`lead-${lead.phone}`} className="lead-card">
                      <span className="lead-index">#{leadsPage * LEADS_PAGE_SIZE + index + 1}</span>
                      <div className="lead-header">
                        <div className="lead-avatar">
                          {(lead.name || lead.business_name || 'C')[0].toUpperCase()}
                        </div>
                        <div className="lead-info">
                          <h4>{lead.name || lead.business_name || 'Unknown'}</h4>
                          <span className={`lead-status status-${lead.status}`}>{lead.status}</span>
                        </div>
                      </div>
                      <div className="lead-details">
                        <div className="lead-row">
                          <Phone size={14} />
                          <span>{lead.phone}</span>
                        </div>
                        {lead.city && (
                          <div className="lead-row">
                            <span>📍 {lead.city}{lead.zone ? ` · ${lead.zone}` : ''}</span>
                          </div>
                        )}
                        {lead.requirement && (
                          <div className="lead-row">
                            <span>📝 {lead.requirement}</span>
                          </div>
                        )}
                        <div className="lead-tags">
                          {lead.category && <span className="template-tag">{lead.category}</span>}
                          {lead.source && <span className="template-tag">{lead.source}</span>}
                          {lead.lead_status && <span className="template-tag">{lead.lead_status}</span>}
                        </div>
                        <div className="lead-row">
                          <span className="lead-date">{formatDate(lead.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {(() => {
                  const totalPages = Math.max(1, Math.ceil(leadsTotal / LEADS_PAGE_SIZE));
                  const pageStart = leadsTotal === 0 ? 0 : leadsPage * LEADS_PAGE_SIZE + 1;
                  const pageEnd = Math.min(leadsTotal, leadsPage * LEADS_PAGE_SIZE + leads.length);
                  return (
                    <div className="product-list-toolbar">
                      <p className="product-section-label">{pageStart}-{pageEnd} of {leadsTotal}</p>
                      <div className="product-pagination">
                        <button
                          type="button"
                          className="save-product-btn compact"
                          onClick={() => fetchLeads({ page: Math.max(0, leadsPage - 1) })}
                          disabled={leadsPage <= 0 || loading}
                        >
                          Previous
                        </button>
                        <span>Page {leadsPage + 1} of {totalPages}</span>
                        <button
                          type="button"
                          className="save-product-btn compact"
                          onClick={() => fetchLeads({ page: Math.min(totalPages - 1, leadsPage + 1) })}
                          disabled={leadsPage >= totalPages - 1 || loading}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        )}

        {activeTab === 'stats' && (
          <div className="stats-section">
            <h2>Bot Statistics</h2>
            <div className="stats-grid">
              <div className="stat-card" data-testid="stat-total-users">
                <Users size={32} />
                <div className="stat-info">
                  <span className="stat-number">{stats.total_users || 0}</span>
                  <span className="stat-title">Total Users</span>
                </div>
              </div>
              <div className="stat-card" data-testid="stat-total-messages">
                <MessageSquare size={32} />
                <div className="stat-info">
                  <span className="stat-number">{stats.total_messages || 0}</span>
                  <span className="stat-title">Total Messages</span>
                </div>
              </div>
              <div className="stat-card" data-testid="stat-total-leads">
                <Users size={32} />
                <div className="stat-info">
                  <span className="stat-number">{stats.total_leads || 0}</span>
                  <span className="stat-title">Total Leads</span>
                </div>
              </div>
              <div className="stat-card inbound" data-testid="stat-inbound">
                <div className="stat-info">
                  <span className="stat-number">{stats.inbound_messages || 0}</span>
                  <span className="stat-title">Inbound Messages</span>
                </div>
              </div>
              <div className="stat-card outbound" data-testid="stat-outbound">
                <div className="stat-info">
                  <span className="stat-number">{stats.outbound_messages || 0}</span>
                  <span className="stat-title">Outbound Messages</span>
                </div>
              </div>
            </div>

            <div className="bot-status">
              <h3>Bot Configuration</h3>
              <div className="config-info">
                <div className="config-row">
                  <span>WhatsApp Number:</span>
                  <span className="config-value">+91 95495 49339</span>
                </div>
                <div className="config-row">
                  <span>Bot Status:</span>
                  <span className="config-value active">Active ✓</span>
                </div>
                <div className="config-row">
                  <span>AI Model:</span>
                  <span className="config-value">GPT-4o</span>
                </div>
                <div className="config-row">
                  <span>Languages:</span>
                  <span className="config-value">English, Hindi</span>
                </div>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'users' && (
          <UsersTab apiUrl={API_URL} currentUser={authUser} />
        )}
        {activeTab === 'lead-import' && (
          <LeadImportTab apiUrl={API_URL} />
        )}
        {activeTab === 'routing' && (
          <RoutingTab
            apiUrl={API_URL}
            user={authUser}
            initialLeadId={deepLinkLeadId}
            onInitialHandled={() => setDeepLinkLeadId('')}
          />
        )}
        {activeTab === 'campaign-manager' && (
          <CampaignManagerTab
            apiUrl={API_URL}
            refreshMs={DASHBOARD_REFRESH_MS}
            onOpenLeadImport={() => selectTab('lead-import')}
          />
        )}
        {activeTab === 'campaign-monthly' && (
          <CampaignMonthlyTab apiUrl={API_URL} />
        )}
        {activeTab === 'templates' && (
          <TemplatesTab apiUrl={API_URL} />
        )}
        {activeTab === 'products' && (
          <div className="products-section">
            {/* Header */}
            <div className="products-header">
              <h2>Products Catalog</h2>
              <div className="products-actions">
                <label className="catalog-upload-btn">
                  <Upload size={16} />
                  {catalogUploadFile ? catalogUploadFile.name : 'Catalog JSON'}
                  <input
                    type="file"
                    accept="application/json,.json"
                    style={{ display: 'none' }}
                    onChange={(e) => setCatalogUploadFile(e.target.files[0] || null)}
                  />
                </label>
                <button
                  className="save-product-btn compact"
                  onClick={handleUploadCatalog}
                  disabled={!catalogUploadFile || catalogUploading}
                >
                  <Save size={16} />
                  {catalogUploading ? 'Uploading...' : 'Upload'}
                </button>
                <input
                  className="product-search-input"
                  type="text"
                  placeholder="Search products..."
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                />
                <button className="save-product-btn compact" onClick={exportProductsCSV} title="Export as CSV">
                  <Download size={16} />
                  Export CSV
                </button>
                <button className="refresh-btn" onClick={() => fetchProducts()}><RefreshCw size={18} /></button>
                <button className="add-product-btn" onClick={startAddProduct}>
                  <span>+</span> Add Product
                </button>
              </div>
            </div>

            <datalist id="collection-options">
              {PRODUCT_SERIES.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
            <datalist id="base-color-options">
              {BASE_COLOR_OPTIONS.map((color) => (
                <option key={color} value={color} />
              ))}
            </datalist>

            {/* Add Product Form (new products only — edits open inline on the card) */}
            {showAddForm && !editingProductId && (
              <div className="add-product-form">
                <div className="form-header">
                  <h3>Add New Product</h3>
                  <button className="form-close" onClick={closeProductForm} aria-label="Close product form">
                    <X size={16} />
                  </button>
                </div>
                <div className="form-grid">
                  <input
                    className="form-input"
                    placeholder="Product Name"
                    value={productForm.name}
                    onChange={(e) => setProductForm((p) => ({ ...p, name: e.target.value }))}
                  />
                  <select
                    className="form-input"
                    value={productForm.type}
                    onChange={(e) => setProductForm((p) => ({ ...p, type: e.target.value }))}
                  >
                    <option value="">Select Type</option>
                    {PRODUCT_TYPES.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  {productForm.type === 'Dealer Showrooms' ? (
                    <select
                      className="form-input"
                      value={productForm.location}
                      onChange={(e) => setProductForm((p) => ({ ...p, location: e.target.value, category: e.target.value }))}
                    >
                      <option value="">Select City / Location</option>
                      {DEALER_SHOWROOM_LOCATIONS.map((loc) => (
                        <option key={loc} value={loc}>{loc}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      className="form-input"
                      list="collection-options"
                      placeholder="Collection"
                      value={productForm.category}
                      onChange={(e) => setProductForm((p) => ({ ...p, category: e.target.value }))}
                    />
                  )}
                  <input
                    className="form-input"
                    placeholder="Price (e.g., 5,000)"
                    value={productForm.price}
                    onChange={(e) => setProductForm((p) => ({ ...p, price: e.target.value }))}
                  />
                  <label className="form-upload-btn">
                    <Upload size={15} />
                    {productFormImage ? productFormImage.name : editingProductId ? 'Replace Image' : 'Upload Image'}
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const file = e.target.files[0] || null;
                        if (validateProductImage(file)) {
                          setProductFormImage(file);
                        } else {
                          e.target.value = '';
                          setProductFormImage(null);
                        }
                      }}
                    />
                  </label>
                  <input
                    className="form-input"
                    placeholder="Buy Link (e.g., haique.world/product/123)"
                    value={productForm.buy_link}
                    onChange={(e) => setProductForm((p) => ({ ...p, buy_link: e.target.value }))}
                  />
                  <input
                    className="form-input"
                    list="base-color-options"
                    placeholder="Base Color Tags (e.g., White, Grey)"
                    value={productForm.color_family}
                    onChange={(e) => setProductForm((p) => ({ ...p, color_family: e.target.value }))}
                  />
                  <textarea
                    className="form-input"
                    placeholder={'Bot recommendation text (include product context). Example: "Bianco Thassos. Base tags: Neo Marble · London Series. ..."'}
                    value={productForm.description}
                    onChange={(e) => setProductForm((p) => ({ ...p, description: e.target.value }))}
                    rows={3}
                  />
                </div>
                <button className="save-product-btn" onClick={handleSaveProduct} disabled={productSaving}>
                    <Save size={16} />
                  {productSaving ? 'Saving...' : 'Save Product'}
                </button>
              </div>
            )}

            {/* Product Grid */}
            {loading ? (
              <div className="loading">Loading…</div>
            ) : (
              <>
                {/* Collections summary */}
                {collections.length > 0 && (
                  <div className="collections-summary">
                    <p className="product-section-label">Collections ({collections.length})</p>
                    <div className="collections-tag-list">
                      {collections.map((col) => {
                        const count = products.filter((p) => (p.category || p.collection) === col).length;
                        return (
                          <span key={col} className="collection-tag">
                            {col} <span className="collection-tag-count">{count}</span>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Manually added products */}
                {products.length > 0 && (() => {
                  const totalPages = Math.max(1, Math.ceil(productTotal / PRODUCT_PAGE_SIZE));
                  const pageStart = productTotal === 0 ? 0 : productPage * PRODUCT_PAGE_SIZE + 1;
                  const pageEnd = Math.min(productTotal, productPage * PRODUCT_PAGE_SIZE + products.length);
                  return (
                    <>
                      <div className="product-list-toolbar">
                        <p className="product-section-label">
                          All Products ({pageStart}-{pageEnd} of {productTotal})
                        </p>
                        <div className="product-pagination">
                          <button
                            type="button"
                            className="save-product-btn compact"
                            onClick={() => fetchProducts({ page: Math.max(0, productPage - 1) })}
                            disabled={productPage <= 0 || loading}
                          >
                            Previous
                          </button>
                          <span>Page {productPage + 1} of {totalPages}</span>
                          <button
                            type="button"
                            className="save-product-btn compact"
                            onClick={() => fetchProducts({ page: Math.min(totalPages - 1, productPage + 1) })}
                            disabled={productPage >= totalPages - 1 || loading}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                      <div className="product-grid">
                        {products.map((product) => (
                        <div key={product.id} className="product-card" id={`manual-card-${product.id}`}>
                          <div className="product-card-img">
                            {product.image_url
                              ? <img src={resolveMediaUrl(product.image_url)} alt={product.name} />
                              : <div className="product-img-placeholder"><Package size={40} /></div>
                            }
                            <button className="product-edit-btn" onClick={() => startEditProduct(product)} title="Edit" aria-label={`Edit ${product.name}`}>
                              <Pencil size={14} />
                            </button>
                            <button className="product-delete-btn" onClick={() => handleDeleteProduct(product.id)} title="Delete" aria-label={`Delete ${product.name}`}>
                              <X size={14} />
                            </button>
                          </div>
                          <div className="product-card-body">
                            <h4>{product.name}</h4>
                            <span className="product-category">{product.category}</span>
                            {product.color_family?.length ? (
                              <div className="product-color-tags">
                                {product.color_family.map((tag) => (
                                  <span key={tag} className="product-color-tag">{tag}</span>
                                ))}
                              </div>
                            ) : null}
                            {product.baseTags
                              ? <p className="product-desc small-muted">Internal tags: {product.baseTags}</p>
                              : null}
                            {product.price && <p className="product-price">₹{product.price}</p>}
                            {(product.description || '').trim() || product.recSeed
                              ? <p className="product-desc">{(product.description || '').trim() || product.recSeed}</p>
                              : <p className="product-desc muted">No recommendation description yet.</p>
                            }
                            <button
                              type="button"
                              className="product-desc-edit"
                              onClick={() => startEditProduct(product)}
                            >
                              Edit recommendation description
                            </button>
                            {product.buy_link && (
                              <a className="product-link" href={product.buy_link.startsWith('http') ? product.buy_link : `https://${product.buy_link}`} target="_blank" rel="noreferrer">
                                Buy Link →
                              </a>
                            )}
                          </div>
                          {manualEditInline && editingProductId === product.id && (
                            <div className="product-inline-editor">
                              <div className="product-inline-editor-header">
                                <h4>Edit product</h4>
                                <button type="button" className="form-close" onClick={closeProductForm} aria-label="Close product editor">
                                  <X size={16} />
                                </button>
                              </div>
                              <div className="form-grid product-inline-form-grid">
                                <input
                                  className="form-input"
                                  placeholder="Product Name"
                                  value={productForm.name}
                                  onChange={(e) => setProductForm((p) => ({ ...p, name: e.target.value }))}
                                />
                                <select
                                  className="form-input"
                                  value={productForm.type}
                                  onChange={(e) => setProductForm((p) => ({ ...p, type: e.target.value }))}
                                >
                                  <option value="">Select Type</option>
                                  {PRODUCT_TYPES.map((type) => (
                                    <option key={type} value={type}>{type}</option>
                                  ))}
                                </select>
                                {productForm.type === 'Dealer Showrooms' ? (
                                  <select
                                    className="form-input"
                                    value={productForm.location}
                                    onChange={(e) => setProductForm((p) => ({ ...p, location: e.target.value, category: e.target.value }))}
                                  >
                                    <option value="">Select City / Location</option>
                                    {DEALER_SHOWROOM_LOCATIONS.map((loc) => (
                                      <option key={loc} value={loc}>{loc}</option>
                                    ))}
                                  </select>
                                ) : (
                                  <input
                                    className="form-input"
                                    list="collection-options"
                                    placeholder="Collection"
                                    value={productForm.category}
                                    onChange={(e) => setProductForm((p) => ({ ...p, category: e.target.value }))}
                                  />
                                )}
                                <input
                                  className="form-input"
                                  placeholder="Price (e.g., 5,000)"
                                  value={productForm.price}
                                  onChange={(e) => setProductForm((p) => ({ ...p, price: e.target.value }))}
                                />
                                <label className="form-upload-btn">
                                  <Upload size={15} />
                                  {productFormImage ? productFormImage.name : 'Replace Image'}
                                  <input
                                    type="file"
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                    onChange={(e) => {
                                      const file = e.target.files[0] || null;
                                      if (validateProductImage(file)) {
                                        setProductFormImage(file);
                                      } else {
                                        e.target.value = '';
                                        setProductFormImage(null);
                                      }
                                    }}
                                  />
                                </label>
                                <input
                                  className="form-input"
                                  placeholder="Buy Link"
                                  value={productForm.buy_link}
                                  onChange={(e) => setProductForm((p) => ({ ...p, buy_link: e.target.value }))}
                                />
                                <input
                                  className="form-input"
                                  list="base-color-options"
                                  placeholder="Base Color Tags (e.g., White, Grey)"
                                  value={productForm.color_family}
                                  onChange={(e) => setProductForm((p) => ({ ...p, color_family: e.target.value }))}
                                />
                                <textarea
                                  className="form-input"
                                  placeholder="Bot recommendation text: Name / Base tags lines, then your notes for matching."
                                  value={productForm.description}
                                  onChange={(e) => setProductForm((p) => ({ ...p, description: e.target.value }))}
                                  rows={4}
                                />
                              </div>
                              <div className="form-actions product-inline-form-actions">
                                <button className="save-product-btn" type="button" onClick={handleSaveProduct} disabled={productSaving}>
                                  <Save size={16} />
                                  {productSaving ? 'Saving...' : 'Update Product'}
                                </button>
                                <button className="save-product-btn compact" type="button" onClick={closeProductForm} disabled={productSaving}>
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                        ))}
                      </div>
                    </>
                  );
                })()}

                {/* Catalog products from JSON */}
                {catalogProducts.length > 0 && (
                  <>
                    <p className="product-section-label">From Catalog JSON</p>
                    <div className="product-grid">
                      {catalogProducts.map((product) => (
                        <div key={product.id} className="product-card catalog-card" id={`catalog-card-${product.designId}`}>
                          <div className="product-card-img">
                            {product.image_url
                              ? <img src={product.image_url} alt={product.name} referrerPolicy="no-referrer" />
                              : <div className="product-img-placeholder"><Package size={40} /></div>
                            }
                            <button
                              className="product-edit-btn"
                              onClick={() => openCatalogRecommendationEditor(product)}
                              title="Edit bot recommendation text"
                              aria-label={`Edit recommendation for ${product.name}`}
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              className="product-delete-btn"
                              onClick={() => deleteCatalogRecommendation(product)}
                              title="Remove saved recommendation text"
                              aria-label={`Remove recommendation for ${product.name}`}
                            >
                              <X size={14} />
                            </button>
                          </div>
                          <div className="product-card-body">
                            <h4>{product.name}</h4>
                            <span className="product-category">{product.category}</span>
                            {product.baseTags
                              ? <p className="product-desc small-muted">Internal tags: {product.baseTags}</p>
                              : null}
                            <p className="product-desc">{(product.description || '').trim()}</p>
                            <button
                              type="button"
                              className="product-desc-edit"
                              onClick={() => openCatalogRecommendationEditor(product)}
                            >
                              Edit bot recommendation text
                            </button>
                            {product.buy_link && (
                              <a className="product-link" href={product.buy_link.startsWith('http') ? product.buy_link : `https://${product.buy_link}`} target="_blank" rel="noreferrer">
                                Catalogue →
                              </a>
                            )}
                          </div>
                          {catalogRecEditor.open && catalogRecEditor.designId === product.designId && (
                            <div className="product-inline-editor catalog-inline-editor">
                              <div className="product-inline-editor-header">
                                <h4>Bot recommendation text</h4>
                                <button
                                  type="button"
                                  className="form-close"
                                  onClick={closeCatalogRecommendationEditor}
                                  aria-label="Close catalog recommendation editor"
                                >
                                  <X size={16} />
                                </button>
                              </div>
                              <p className="product-rec-subtitle">{catalogRecEditor.productLabel}</p>
                              <div className="catalog-rec-fixed">
                                <div><span className="catalog-rec-k">Name</span>{catalogRecEditor.productName}</div>
                                <div>
                                  <span className="catalog-rec-k">Internal tags</span>
                                  {(catalogRecEditor.baseTags || '').trim() || '—'}
                                </div>
                              </div>
                              <textarea
                                className="form-input"
                                rows={5}
                                placeholder="Add extra keywords / guidance for the bot (name + base tags are always included automatically)."
                                value={catalogRecEditor.extra}
                                onChange={(e) => setCatalogRecEditor((prev) => ({ ...prev, extra: e.target.value }))}
                              />
                              <div className="form-actions product-inline-form-actions">
                                <button className="save-product-btn" type="button" onClick={saveCatalogRecommendation} disabled={catalogRecSaving}>
                                  <Save size={16} />
                                  {catalogRecSaving ? 'Saving...' : 'Save'}
                                </button>
                                <button className="save-product-btn compact" type="button" onClick={closeCatalogRecommendationEditor} disabled={catalogRecSaving}>
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {products.length === 0 && catalogProducts.length === 0 && (
                  <div className="empty-state">
                    <Package size={48} />
                    <p>No products yet</p>
                    <span>Click \"+ Add Product\" to add your first product</span>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'prompts' && (
          <div className="prompt-section">
            <div className="prompt-header">
              <div>
                <h2>Bot System Prompt</h2>
                <p className="prompt-subtitle">Edit the instructions Samara follows. Changes take effect instantly.</p>
              </div>
              <button
                className="add-product-btn"
                onClick={handleSavePrompt}
                disabled={promptSaving || !promptText.trim() || !promptDirty}
              >
                <Save size={16} />
                {promptSaving ? 'Saving…' : 'Save Prompt'}
              </button>
            </div>
            <textarea
              className="prompt-textarea"
              value={promptText}
              onChange={(e) => {
                setPromptText(e.target.value);
                setPromptDirty(true);
              }}
              placeholder="Loading prompt…"
            />
            <div className="bot-test-panel">
              <div className="bot-test-header">
                <div>
                  <h3>Bot Checks</h3>
                  <p>Runs catalog previews for manuals, certificates, render, installation, and collection image questions.</p>
                </div>
                <button className="save-product-btn compact" onClick={runBotTests} disabled={botTesting}>
                  <RefreshCw size={16} />
                  {botTesting ? 'Running...' : 'Run Checks'}
                </button>
              </div>
              {botTestResults.length > 0 && (
                <div className="bot-test-results">
                  {botTestResults.map((result) => (
                    <div key={result.id} className={`bot-test-result ${result.passed ? 'passed' : 'failed'}`}>
                      <span>{result.passed ? 'PASS' : 'FAIL'}</span>
                      <strong>{result.title}</strong>
                      <p>{result.note}</p>
                    </div>
                  ))}
                </div>
              )}
              {botTestReport && <pre className="bot-test-report">{botTestReport}</pre>}
            </div>
          </div>
        )}

        {/* ── Brochures ─────────────────────────────────────────────── */}
        {activeTab === 'brochures' && (
          <div className="docs-section">
            <div className="docs-header">
              <BookOpen size={22} />
              <h2>Brochures</h2>
            </div>
            <div className="docs-grid">
              {BROCHURES.map((item) => (
                <div key={item.slug} className="doc-card">
                  <div className="doc-card-icon"><FileText size={36} /></div>
                  <h4 className="doc-card-title">{item.label}</h4>
                  <div className="doc-card-actions">
                    <a className="doc-btn" href={pdfUrl(item.kind, item.slug)} target="_blank" rel="noreferrer">View PDF</a>
                    <a className="doc-btn secondary" href={pdfDownloadUrl(item)} download={pdfFilename(item)}>Download</a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Installation Manuals ───────────────────────────────────── */}
        {activeTab === 'installation' && (
          <div className="docs-section">
            <div className="docs-header">
              <Wrench size={22} />
              <h2>Installation Manuals</h2>
            </div>
            <div className="docs-grid">
              {INSTALLATION_MANUALS.map((item) => (
                <div key={item.slug} className="doc-card">
                  <div className="doc-card-icon"><FileText size={36} /></div>
                  <h4 className="doc-card-title">{item.label}</h4>
                  <div className="doc-card-actions">
                    <a className="doc-btn" href={pdfUrl(item.kind, item.slug)} target="_blank" rel="noreferrer">View PDF</a>
                    <a className="doc-btn secondary" href={pdfDownloadUrl(item)} download={pdfFilename(item)}>Download</a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Technical Specifications ───────────────────────────────── */}
        {activeTab === 'techspecs' && (
          <div className="docs-section">
            <div className="docs-header">
              <ClipboardList size={22} />
              <h2>Technical Specifications</h2>
            </div>
            <div className="docs-grid">
              {TECHNICAL_SPECS.map((item) => (
                <div key={item.slug} className="doc-card">
                  <div className="doc-card-icon"><FileText size={36} /></div>
                  <h4 className="doc-card-title">{item.label}</h4>
                  <div className="doc-card-actions">
                    <a className="doc-btn" href={pdfUrl(item.kind, item.slug)} target="_blank" rel="noreferrer">View PDF</a>
                    <a className="doc-btn secondary" href={pdfDownloadUrl(item)} download={pdfFilename(item)}>Download</a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </main>
    </div>
  );
}

export default App;
